import type {
	ActivitySummaryResponse,
	AiResetConflictResponse,
	AiResetResponse,
	AiStatusEnvelope,
	CheckWorkOrderResponse,
	CreateReconciliationSetRequest,
	EligibleFilesResponse,
	FileListResponse,
	FileResponse,
	GetActivitiesResponse,
	GetActivityDetailsResponse,
	GetUnitSynonymsResponse,
	ListReconciliationSetsResponse,
	PreflightResult,
	PrepareResult,
	RateDetailResponse,
	RateListResponse,
	ReconciliationSet,
	ReconciliationSetFileRole,
	ReconciliationStatus,
	RunListItem,
	RunReport,
	RunReportLimitExceeded,
	UnitSynonym,
	UpdateReconciliationSetRequest,
} from "@efes/shared";
import { pdfExportMessages, reconciliationMessages } from "./messages.js";

/**
 * Discriminated union ответа `runReconciliation` (фича 023, FR-028a).
 * 202 → `started`; 409 → `in-progress` (concurrent run guard, FR-022).
 */
export type RunResponse =
	| { status: "started"; taskId: string; runId: string }
	| { status: "in-progress"; existingRunId: string; taskId: string };

/**
 * Ошибка 404 для отчёта прогона. UI рендерит fullscreen «Отчёт не найден».
 */
export class RunNotFoundError extends Error {
	constructor(message?: string) {
		super(message ?? "Run report not found");
		this.name = "RunNotFoundError";
	}
}

const BASE = "/api";

/**
 * Загружает пакет файлов на сервер.
 * Возвращает uploadId для отслеживания прогресса через polling (useTaskEvents).
 */
export async function uploadFiles(files: File[]): Promise<{ uploadId: string }> {
	const formData = new FormData();
	for (const file of files) {
		formData.append("files", file);
	}

	const response = await fetch(`${BASE}/files/upload`, {
		method: "POST",
		body: formData,
	});

	if (!response.ok) {
		const err = await response.json();
		throw new Error(err.message || "Upload failed");
	}

	return response.json();
}

/** Получить список файлов с фильтрацией */
export async function getFiles(filters?: {
	type?: string;
	objectName?: string;
	periodYear?: number;
	periodMonth?: number;
}): Promise<FileListResponse> {
	const params = new URLSearchParams();
	if (filters?.type) params.set("type", filters.type);
	if (filters?.objectName) params.set("objectName", filters.objectName);
	if (filters?.periodYear) params.set("periodYear", String(filters.periodYear));
	if (filters?.periodMonth) params.set("periodMonth", String(filters.periodMonth));

	const url = `${BASE}/files${params.toString() ? `?${params}` : ""}`;
	const res = await fetch(url);
	if (!res.ok) throw new Error("Failed to fetch files");
	return res.json();
}

/** Получить детали одного файла */
export async function getFile(id: number): Promise<FileResponse> {
	const res = await fetch(`${BASE}/files/${id}`);
	if (!res.ok) throw new Error(`File ${id} not found`);
	return res.json();
}

// ─── LLM-ревью акта (Sprint 4 + Фича 035) ──────────────────────────────────
// Типы централизованы в @efes/shared (Constitution VIII — единый источник
// правды BE/FE). Runtime Zod-валидация ответов — TODO: vitest пока не
// подтягивает новые экспорты shared через статический import.

import type { ActLlmStatusResponse, LlmBlocksResponse } from "@efes/shared";

export type {
	ActLlmStatusResponse,
	LlmBlockCounts,
	LlmBlockDetail,
	LlmBlocksResponse,
	LlmReviewReason,
	LlmReviewStatus,
} from "@efes/shared";

/**
 * Счётчики LLM-ревизии блоков акта.
 * Поле `skippedWithConcern` (фича 035): число `skipped` блоков с
 * reason ≠ `no_coded_line` (т.е. «реально-проблемных», в отличие от штатных
 * пропусков «нечего проверять»). UI-иконка различает их визуально.
 */
export async function getActLlmStatus(fileId: number): Promise<ActLlmStatusResponse> {
	const res = await fetch(`${BASE}/files/${fileId}/llm-status`);
	if (!res.ok) throw new Error(`LLM status fetch failed for file ${fileId}`);
	return res.json();
}

export async function getFileLlmBlocks(fileId: number): Promise<LlmBlocksResponse> {
	const res = await fetch(`${BASE}/files/${fileId}/llm-blocks`);
	if (!res.ok) throw new Error(`LLM blocks fetch failed for file ${fileId}`);
	// TODO: LlmBlocksResponseSchema.parse — пока vitest не подтягивает новые
	// экспорты shared через статический import (см. комментарий в backend).
	return res.json();
}

/**
 * Возвращает сырой `llm_review_json` блока в виде строки (для inline-раскрытия
 * деталей в UI). Frontend сам делает `JSON.parse` для типизированного рендера
 * + опционально показывает raw-форму для копирования.
 */
export async function getFileLlmBlockRaw(fileId: number, blockId: number): Promise<string> {
	const res = await fetch(`${BASE}/files/${fileId}/llm-blocks/${blockId}/raw`);
	if (!res.ok) throw new Error(`LLM block raw fetch failed for ${fileId}/${blockId}`);
	return res.text();
}

/**
 * Sprint 4 / A6.5c — агрегированные счётчики LLM-ревизии для всех актов набора.
 * Используется в run-confirm dialog (POST /run preflight).
 */
export interface ReconciliationLlmReadinessResponse {
	setId: number;
	total: number;
	none: number;
	pending: number;
	applied: number;
	skipped: number;
	failed: number;
}

export async function getReconciliationLlmReadiness(
	setId: number,
): Promise<ReconciliationLlmReadinessResponse> {
	const res = await fetch(`${BASE}/reconciliation/sets/${setId}/llm-readiness`);
	if (!res.ok) throw new Error(`LLM readiness fetch failed for set ${setId}`);
	return res.json();
}

// ─── Completion act: иерархический результат парсинга v3 ─────────────
// Форма ответа соответствует хэндлеру `GET /api/files/:id/parse-result`
// в `apps/api/src/routes/files.ts` (строки ~292–462). Типы описаны здесь,
// а не в `@efes/shared`, чтобы избежать дрейфа: `ParsedActSchema` в shared
// описывает внутреннее представление парсера (snake_case, raw_json),
// тогда как эндпоинт возвращает плоскую БД-форму (camelCase, excelRow,
// orderIndex, isImplicit как boolean).

export interface CompletionActParseResultFile {
	id: number;
	originalName: string;
	sheetName: string | null;
	fileType: string;
	status: string;
	parserVersion: string | null;
}

export interface CompletionActParseResultMaterial {
	materialName: string;
	unit: string | null;
	norm: number | null;
	totalConsumption: number;
	price: number | null;
	cost: number;
	excelRow: number;
	orderIndex: number;
}

export interface CompletionActParseResultExtraLabor {
	unit: string | null;
	qty: number;
	rate: number;
	total: number;
	excelRow: number;
}

export interface CompletionActParseResultWork {
	rowNumber: number;
	code: string | null;
	workName: string;
	unit: string | null;
	quantity: number;
	salaryRate: number;
	salaryTotal: number;
	excelRow: number;
	continuationRows: number[];
	materials: CompletionActParseResultMaterial[];
	extraLabor: CompletionActParseResultExtraLabor[];
}

export interface CompletionActParseResultBlock {
	blockNumber: number;
	sectionHeader: string | null;
	sectionHeaderRow: number | null;
	subtotalSalary: number;
	subtotalMaterials: number;
	subtotalTotal: number;
	subtotalRow: number | null;
	isImplicit: boolean;
	works: CompletionActParseResultWork[];
}

export interface CompletionActParseResultValidation {
	arithmeticOk: boolean;
	subtotalsOk: boolean;
	grandTotalOk: boolean;
	grandFotDeclared: number | null;
	grandMaterialsDeclared: number | null;
	grandTotalDeclared: number | null;
	grandFotComputed: number;
	grandMaterialsComputed: number;
	grandDeclaredKind: string | null;
	hybridRowsCount: number;
	issues: unknown[];
	warnings: unknown[];
}

export interface CompletionActParseResult {
	file: CompletionActParseResultFile;
	blocks: CompletionActParseResultBlock[];
	validation: CompletionActParseResultValidation | null;
}

/**
 * Получить иерархический результат парсинга акта v3 (blocks → works → materials/extraLabor).
 * Используется UI (US4) для отрисовки результата без повторного парсинга файла.
 */
export async function getCompletionActParseResult(
	fileId: number,
): Promise<CompletionActParseResult> {
	const res = await fetch(`${BASE}/files/${fileId}/parse-result`, {
		credentials: "include",
	});
	if (!res.ok) {
		const text = await res.text().catch(() => "");
		throw new Error(text || `HTTP ${res.status}`);
	}
	return res.json();
}

/**
 * Удаление файла (hard delete).
 *
 * `cascadeDeletedSetIds` — ID наборов сверки, которые автоматически удалились
 * как побочный эффект (последний файл набора). Caller должен закрыть открытые
 * табы детали/результата по этим setId через `closeTabsForSet`.
 */
export async function deleteFile(
	id: number,
): Promise<{ id: number; deleted: boolean; cascadeDeletedSetIds: number[] }> {
	const res = await fetch(`${BASE}/files/${id}`, { method: "DELETE" });
	if (!res.ok) throw new Error(`Failed to delete file ${id}`);
	const body = await res.json();
	return {
		id: body.id,
		deleted: body.deleted,
		cascadeDeletedSetIds: body.cascadeDeletedSetIds ?? [],
	};
}

/** Отправить выбор листов для файла, ожидающего выбора */
export async function selectSheet(
	uploadId: string,
	fileIndex: number,
	sheetNames?: string[],
	cancelled?: boolean,
): Promise<{ ok: boolean }> {
	const res = await fetch(`${BASE}/files/select-sheet`, {
		method: "POST",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify({ uploadId, fileIndex, sheetNames, cancelled: cancelled || undefined }),
	});
	if (!res.ok) {
		const err = await res.json().catch(() => ({ message: "Failed to select sheet" }));
		throw new Error(err.message || "Failed to select sheet");
	}
	return res.json();
}

/** Отправить решение по конфликту (дубликат или rate_book) */
export async function resolveConflict(
	uploadId: string,
	fileIndex: number,
	action: "replace" | "cancel",
): Promise<{ ok: boolean }> {
	const res = await fetch(`${BASE}/files/resolve-conflict`, {
		method: "POST",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify({ uploadId, fileIndex, action }),
	});
	if (!res.ok) {
		const err = await res.json().catch(() => ({ message: "Failed to resolve conflict" }));
		throw new Error(err.message || "Failed to resolve conflict");
	}
	return res.json();
}

/** Получить расценки по файлу с поиском и пагинацией */
export async function getRates(
	fileId: number,
	search?: string,
	page = 1,
	pageSize = 50,
): Promise<RateListResponse> {
	const params = new URLSearchParams({
		fileId: String(fileId),
		page: String(page),
		pageSize: String(pageSize),
	});
	if (search) params.set("search", search);

	const res = await fetch(`${BASE}/rates?${params}`);
	if (!res.ok) throw new Error("Failed to fetch rates");
	return res.json();
}

/** Получить детали расценки */
export async function getRate(id: number): Promise<RateDetailResponse> {
	const res = await fetch(`${BASE}/rates/${id}`);
	if (!res.ok) throw new Error(`Rate ${id} not found`);
	return res.json();
}

/** Запустить проверку наряда по книге расценок */
export async function checkWorkOrder(fileId: number): Promise<CheckWorkOrderResponse> {
	const res = await fetch(`${BASE}/verification/check-work-order`, {
		method: "POST",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify({ fileId }),
	});
	if (!res.ok) {
		const err = await res.json().catch(() => ({ message: "Ошибка проверки наряда" }));
		throw new Error(err.message || "Ошибка проверки наряда");
	}
	return res.json();
}

/** Добавить синоним единицы измерения */
export async function addUnitSynonym(
	canonicalUnit: string,
	synonym: string,
): Promise<{ id: number }> {
	const res = await fetch(`${BASE}/unit-synonyms`, {
		method: "POST",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify({ canonicalUnit, synonym }),
	});
	if (!res.ok) {
		const err = await res.json().catch(() => ({ message: "Ошибка добавления синонима" }));
		throw new Error(err.message || "Ошибка добавления синонима");
	}
	return res.json();
}

/** Получить все синонимы единиц измерения */
export async function getUnitSynonyms(): Promise<GetUnitSynonymsResponse> {
	const res = await fetch(`${BASE}/unit-synonyms`);
	if (!res.ok) throw new Error("Failed to fetch unit synonyms");
	return res.json();
}

/** Получить синоним по ID */
export async function getUnitSynonymById(id: number): Promise<UnitSynonym> {
	const res = await fetch(`${BASE}/unit-synonyms/${id}`);
	if (!res.ok) throw new Error(`Unit synonym ${id} not found`);
	return res.json();
}

/** Обновить синоним единицы измерения */
export async function updateUnitSynonym(id: number, synonym: string): Promise<void> {
	const res = await fetch(`${BASE}/unit-synonyms/${id}`, {
		method: "PATCH",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify({ synonym }),
	});
	if (!res.ok) {
		const err = await res.json().catch(() => ({ message: "Ошибка обновления синонима" }));
		throw new Error(err.message || "Ошибка обновления синонима");
	}
}

/** Удалить синоним единицы измерения */
export async function deleteUnitSynonym(id: number): Promise<void> {
	const res = await fetch(`${BASE}/unit-synonyms/${id}`, {
		method: "DELETE",
	});
	if (!res.ok) {
		const err = await res.json().catch(() => ({ message: "Ошибка удаления синонима" }));
		throw new Error(err.message || "Ошибка удаления синонима");
	}
}

export interface PinoLogEntry {
	time: number;
	level: number;
	msg: string;
	[key: string]: unknown;
}

/** Получить Pino-логи для конкретного действия */
export async function getActivityLogs(
	activityId: number,
): Promise<{ logs: PinoLogEntry[]; total: number }> {
	const res = await fetch(`${BASE}/activities/${activityId}/logs`);
	if (!res.ok) throw new Error("Failed to fetch activity logs");
	return res.json();
}

/** Получить список действий с фильтрацией и пагинацией */
export async function getActivities(params?: {
	cursor?: string;
	limit?: number;
	type?: string;
	status?: string;
	from?: string;
	to?: string;
	sort?: "asc" | "desc";
}): Promise<GetActivitiesResponse> {
	const searchParams = new URLSearchParams();
	if (params?.cursor) searchParams.set("cursor", params.cursor);
	if (params?.limit) searchParams.set("limit", String(params.limit));
	if (params?.type) searchParams.set("type", params.type);
	if (params?.status) searchParams.set("status", params.status);
	if (params?.from) searchParams.set("from", params.from);
	if (params?.to) searchParams.set("to", params.to);
	if (params?.sort) searchParams.set("sort", params.sort);

	const url = `${BASE}/activities${searchParams.toString() ? `?${searchParams}` : ""}`;
	const res = await fetch(url);
	if (!res.ok) throw new Error("Failed to fetch activities");
	return res.json();
}

/** Получить детали действия */
export async function getActivityDetails(
	activityId: number,
	params?: { limit?: number; offset?: number },
): Promise<GetActivityDetailsResponse> {
	const searchParams = new URLSearchParams();
	if (params?.limit) searchParams.set("limit", String(params.limit));
	if (params?.offset) searchParams.set("offset", String(params.offset));

	const url = `${BASE}/activities/${activityId}/details${searchParams.toString() ? `?${searchParams}` : ""}`;
	const res = await fetch(url);
	if (!res.ok) throw new Error(`Failed to fetch details for activity ${activityId}`);
	return res.json();
}

/** Получить сводку действий (группировка) */
export async function getActivitySummary(params: {
	groupBy: "type" | "status";
	type?: string;
	status?: string;
	from?: string;
	to?: string;
}): Promise<ActivitySummaryResponse> {
	const searchParams = new URLSearchParams({ groupBy: params.groupBy });
	if (params.type) searchParams.set("type", params.type);
	if (params.status) searchParams.set("status", params.status);
	if (params.from) searchParams.set("from", params.from);
	if (params.to) searchParams.set("to", params.to);

	const res = await fetch(`${BASE}/activities/summary?${searchParams}`);
	if (!res.ok) throw new Error("Failed to fetch activity summary");
	return res.json();
}

// ─── Reconciliation sets (feature 020) ─────────────────────────────────
// Все эндпойнты требуют аутентификации (cookie session) — используется
// `credentials: "include"`, чтобы fetch отправил cookies на API.

/**
 * POST /api/reconciliation/sets
 * Создаёт новый набор сверки (наряды + акты + level + modes).
 */
export async function createReconciliationSet(
	input: CreateReconciliationSetRequest,
): Promise<ReconciliationSet> {
	const res = await fetch(`${BASE}/reconciliation/sets`, {
		method: "POST",
		credentials: "include",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify(input),
	});
	if (!res.ok) {
		const err = await res
			.json()
			.catch(() => ({ message: reconciliationMessages.error_create_failed }));
		throw new Error(err.message || reconciliationMessages.error_create_failed);
	}
	return res.json();
}

/**
 * GET /api/reconciliation/sets
 * Возвращает страницу наборов сверки. Параметры передаются через query string.
 */
export async function getReconciliationSets(params?: {
	page?: number;
	pageSize?: number;
	status?: ReconciliationStatus;
	createdBy?: number;
}): Promise<ListReconciliationSetsResponse> {
	const searchParams = new URLSearchParams();
	if (params?.page) searchParams.set("page", String(params.page));
	if (params?.pageSize) searchParams.set("pageSize", String(params.pageSize));
	if (params?.status) searchParams.set("status", params.status);
	if (params?.createdBy) searchParams.set("createdBy", String(params.createdBy));

	const url = `${BASE}/reconciliation/sets${searchParams.toString() ? `?${searchParams}` : ""}`;
	const res = await fetch(url, { credentials: "include" });
	if (!res.ok) throw new Error(reconciliationMessages.error_load_failed);
	return res.json();
}

/**
 * GET /api/reconciliation/sets/:setId
 * Возвращает один набор сверки со всеми связанными файлами.
 */
export async function getReconciliationSet(setId: number): Promise<ReconciliationSet> {
	const res = await fetch(`${BASE}/reconciliation/sets/${setId}`, {
		credentials: "include",
	});
	if (!res.ok) throw new Error(reconciliationMessages.error_load_one_failed);
	return res.json();
}

/**
 * PATCH /api/reconciliation/sets/:setId
 * Обновляет набор сверки. Replace-all семантика: каждое присутствующее поле
 * полностью заменяет текущее значение.
 */
export async function updateReconciliationSet(
	setId: number,
	input: UpdateReconciliationSetRequest,
): Promise<ReconciliationSet> {
	const res = await fetch(`${BASE}/reconciliation/sets/${setId}`, {
		method: "PATCH",
		credentials: "include",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify(input),
	});
	if (!res.ok) {
		const err = await res
			.json()
			.catch(() => ({ message: reconciliationMessages.error_update_failed }));
		throw new Error(err.message || reconciliationMessages.error_update_failed);
	}
	return res.json();
}

/**
 * DELETE /api/reconciliation/sets/:setId
 * Удаляет набор сверки. Сервер возвращает 204 No Content.
 */
export async function deleteReconciliationSet(setId: number): Promise<void> {
	const res = await fetch(`${BASE}/reconciliation/sets/${setId}`, {
		method: "DELETE",
		credentials: "include",
	});
	if (!res.ok) {
		const err = await res
			.json()
			.catch(() => ({ message: reconciliationMessages.error_delete_failed }));
		throw new Error(err.message || reconciliationMessages.error_delete_failed);
	}
}

/**
 * GET /api/reconciliation/eligible-files/:role
 * Возвращает список файлов, доступных для добавления в набор по указанной роли
 * (`work_order` или `act`).
 */
export async function getEligibleFiles(
	role: ReconciliationSetFileRole,
): Promise<EligibleFilesResponse> {
	const res = await fetch(`${BASE}/reconciliation/eligible-files/${role}`, {
		credentials: "include",
	});
	if (!res.ok) throw new Error(reconciliationMessages.error_eligible_failed);
	return res.json();
}

/**
 * POST /api/reconciliation/sets/:setId/validate
 * Read-only preflight-проверка готовности набора к запуску. Не меняет статус.
 */
export async function validateReconciliationSet(setId: number): Promise<PreflightResult> {
	const res = await fetch(`${BASE}/reconciliation/sets/${setId}/validate`, {
		method: "POST",
		credentials: "include",
	});
	if (!res.ok) {
		const err = await res
			.json()
			.catch(() => ({ message: reconciliationMessages.error_validate_failed }));
		throw new Error(err.message || reconciliationMessages.error_validate_failed);
	}
	return res.json();
}

/**
 * POST /api/reconciliation/sets/:setId/prepare
 * Финализирует набор (mutation): переводит статус draft → ready при успехе.
 */
export async function prepareReconciliationSet(setId: number): Promise<PrepareResult> {
	const res = await fetch(`${BASE}/reconciliation/sets/${setId}/prepare`, {
		method: "POST",
		credentials: "include",
	});
	if (!res.ok) {
		const err = await res
			.json()
			.catch(() => ({ message: reconciliationMessages.error_prepare_failed }));
		throw new Error(err.message || reconciliationMessages.error_prepare_failed);
	}
	return res.json();
}

// ─── Reconciliation runs (feature 023, этап 7b) ────────────────────────
// Backend эндпойнты фичи 022 НЕ модифицируются (FR-033). Эти три обёртки —
// клиентский фасад для запуска прогона, чтения отчёта и списка прогонов.

/**
 * POST /api/reconciliation/sets/:setId/run (FR-028).
 * Запускает прогон сверки. Возвращает discriminated union:
 *  - 202 → `{ status: "started", taskId, runId }`
 *  - 409 → `{ status: "in-progress", existingRunId, taskId }` (FR-022, FR-028a).
 * 4xx/5xx (кроме 409) бросают `Error` через стандартный fallback.
 */
export async function runReconciliation(setId: number): Promise<RunResponse> {
	const res = await fetch(`${BASE}/reconciliation/sets/${setId}/run`, {
		method: "POST",
		credentials: "include",
	});

	if (res.status === 202) {
		const body = (await res.json()) as { taskId: string; runId: string };
		return { status: "started", taskId: body.taskId, runId: body.runId };
	}

	if (res.status === 409) {
		const body = (await res.json()) as {
			status: "RUN_IN_PROGRESS";
			existingRunId: string;
			taskId: string;
		};
		return {
			status: "in-progress",
			existingRunId: body.existingRunId,
			taskId: body.taskId,
		};
	}

	const err = await res.json().catch(() => ({ message: reconciliationMessages.toast_api_error }));
	throw new Error(err.message || reconciliationMessages.toast_api_error);
}

/**
 * GET /api/reconciliation/runs/:runId (FR-028).
 * Возвращает отчёт прогона (`RunReport`) либо защитный ответ при превышении
 * лимита (`RunReportLimitExceeded`). 404 → `RunNotFoundError`.
 */
export async function getRunReport(runId: string): Promise<RunReport | RunReportLimitExceeded> {
	const res = await fetch(`${BASE}/reconciliation/runs/${runId}`, {
		credentials: "include",
	});

	if (res.status === 404) {
		throw new RunNotFoundError(reconciliationMessages.error_run_not_found);
	}

	if (!res.ok) {
		const err = await res.json().catch(() => ({ message: reconciliationMessages.toast_api_error }));
		throw new Error(err.message || reconciliationMessages.toast_api_error);
	}

	return (await res.json()) as RunReport | RunReportLimitExceeded;
}

/**
 * GET /api/reconciliation/sets/:setId/runs (FR-028, FR-007).
 * Возвращает массив прогонов набора (отсортированный `started_at DESC`).
 * Backend оборачивает массив в `{ runs }` — обёртка извлекает `.runs`.
 */
export async function getRunsList(setId: number): Promise<RunListItem[]> {
	const res = await fetch(`${BASE}/reconciliation/sets/${setId}/runs`, {
		credentials: "include",
	});

	if (!res.ok) {
		const err = await res
			.json()
			.catch(() => ({ message: reconciliationMessages.error_load_failed }));
		throw new Error(err.message || reconciliationMessages.error_load_failed);
	}

	const body = (await res.json()) as { runs: RunListItem[] };
	return body.runs;
}

/**
 * GET /api/reconciliation/runs/:runId/pdf (фича 034).
 *
 * Качает PDF-отчёт по завершённому прогону. Возвращает Blob + имя файла,
 * извлечённое из `Content-Disposition` (RFC 5987, `filename*=UTF-8''`).
 * UI инициирует загрузку через `<a download={filename}>` после получения.
 *
 * Ошибки сервера мапятся в `Error` с user-facing сообщением (FR-019a) —
 * детали не показываются, только короткая фраза для toast.
 */
export async function downloadRunPdf(runId: string): Promise<{ blob: Blob; filename: string }> {
	const res = await fetch(`${BASE}/reconciliation/runs/${runId}/pdf`, {
		credentials: "include",
	});

	if (!res.ok) {
		// На сервере (FR-019a) — закрытый allow-list полей в details;
		// игнорируем их, показываем только message.
		let serverMessage: string | null = null;
		let serverError: string | null = null;
		try {
			const parsed = (await res.json()) as { error?: string; message?: string };
			serverMessage = parsed.message ?? null;
			serverError = parsed.error ?? null;
		} catch {
			/* not-json: оставим fallback */
		}
		// Подкатегория 412 различается по error-коду: RUN_LIMIT_EXCEEDED ≠ RUN_NOT_COMPLETED
		// (fix M8 из code-review). Без разделения юзер с превышением лимита видел бы
		// «прогон ещё не завершён», что сбивает с толку.
		let fallback = pdfExportMessages.error_generation_failed;
		if (res.status === 412) {
			fallback =
				serverError === "RUN_LIMIT_EXCEEDED"
					? pdfExportMessages.error_run_limit_exceeded
					: pdfExportMessages.error_run_not_completed;
		}
		throw new Error(serverMessage ?? fallback);
	}

	const filename = parseContentDispositionFilename(res.headers.get("Content-Disposition"));
	const blob = await res.blob();
	return { blob, filename };
}

/**
 * Извлекает имя файла из `Content-Disposition` (RFC 5987).
 * Приоритет: `filename*=UTF-8''<encoded>` (поддерживает кириллицу) →
 * `filename="..."` (ASCII fallback). При полном отсутствии — generic-имя.
 */
function parseContentDispositionFilename(headerValue: string | null): string {
	if (!headerValue) return "report.pdf";
	const utf8Match = headerValue.match(/filename\*=UTF-8''([^;]+)/i);
	if (utf8Match) {
		try {
			return decodeURIComponent(utf8Match[1]);
		} catch {
			/* fallthrough */
		}
	}
	const asciiMatch = headerValue.match(/filename="([^"]+)"/i);
	if (asciiMatch) return asciiMatch[1];
	return "report.pdf";
}

// ─────────────────────────────────────────────────────────────────────────
// AI usage indicator (фича 031)
// ─────────────────────────────────────────────────────────────────────────

/**
 * GET /api/ai-status — текущее состояние AI-индикатора.
 */
export async function getAiStatus(): Promise<AiStatusEnvelope> {
	const res = await fetch(`${BASE}/ai-status`, { credentials: "include" });
	if (!res.ok) {
		throw new Error(`Failed to load AI status: ${res.status}`);
	}
	return (await res.json()) as AiStatusEnvelope;
}

/**
 * Ошибка-конфликт при reset'е (модель сменилась во время диалога).
 */
export class AiResetModelMismatchError extends Error {
	constructor(
		public expected: string,
		public actual: string,
	) {
		super(`Model mismatch: expected ${expected}, actual ${actual}`);
		this.name = "AiResetModelMismatchError";
	}
}

/**
 * POST /api/ai-status/reset — сбросить счётчики активной модели (US2).
 * При 409 (модель сменилась) бросает `AiResetModelMismatchError`.
 */
export async function resetAiCounters(expectedModelName: string): Promise<AiResetResponse> {
	const res = await fetch(`${BASE}/ai-status/reset`, {
		method: "POST",
		headers: { "Content-Type": "application/json" },
		credentials: "include",
		body: JSON.stringify({ expectedModelName }),
	});

	if (res.status === 409) {
		const conflict = (await res.json()) as AiResetConflictResponse;
		throw new AiResetModelMismatchError(conflict.expectedModelName, conflict.actualModelName);
	}

	if (!res.ok) {
		throw new Error(`Failed to reset AI counters: ${res.status}`);
	}

	return (await res.json()) as AiResetResponse;
}
